import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormattedMessage, useIntl } from 'react-intl'
import { LuChevronDown, LuCirclePlus } from 'react-icons/lu'
import { theme } from '@/lib/themes'
import { useContactModalStore } from '@/lib/stores/contactModalStore'

import { GET_ALL_LIMIT } from '@/lib/core/constants/constants'
import { SelectField, SelectOption } from '@/lib/core/components/Select'
import { Colors } from '@/lib/core/components/Select/Select.styles'

import messages from '@/lib/features/SmartShares/components/ShareAssetModal/ShareAssetModal.messages'
import { Share } from '@/lib/features/SmartShares/interfaces'
import { useMyContacts } from '@/lib/features/Contacts/api'

import {
  Container,
  ExtendSelectContactItem,
  ExtendSelectContactLabel,
} from './SelectContact.styles'
import { ShareFormState } from '../../ShareAssetModal.types'

export type SelectContactProps = {
  shareToExtend?: Share
  onwardShareToExtend?: Share
}

export const SelectContact = ({ shareToExtend, onwardShareToExtend }: SelectContactProps) => {
  const { formatMessage } = useIntl()

  const { openModal: openContactModal } = useContactModalStore()
  const myContacts = useMyContacts({ pageSize: GET_ALL_LIMIT })

  const { formState, setValue, watch } = useFormContext<ShareFormState>()
  const selectedAssets = watch('assets')

  const options = useMemo(
    () =>
      myContacts.data.results
        .map(
          ({ organizationTo: { id, name } }) =>
            ({
              label: name,
              value: id,
              data: {
                optionType: 'SelectContact',
              },
            }) as SelectOption,
        )
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'en', { caseFirst: 'upper', numeric: true }),
        ),
    [myContacts],
  )

  useEffect(() => {
    if (shareToExtend) {
      setValue('organizationTo', shareToExtend.organization.id)
    } else {
      setValue('organizationTo', null)
    }
  }, [shareToExtend, onwardShareToExtend, setValue])

  return (
    <Container>
      {shareToExtend ? (
        <div>
          <ExtendSelectContactLabel>Who are you sharing with *</ExtendSelectContactLabel>
          <ExtendSelectContactItem>
            {shareToExtend.organization.name}{' '}
            <LuChevronDown size={'24'} color={(theme.colors as Colors).primary} strokeWidth={1.5} />
          </ExtendSelectContactItem>
        </div>
      ) : (
        <SelectField
          aria-labelledby="organizationTo"
          name="organizationTo"
          label={formatMessage(messages.organizationToLabel)}
          placeholder={formatMessage(messages.organizationToPlaceholder)}
          disabled={shareToExtend ? true : formState.isSubmitting}
          required
          selectProps={{
            actionCallback: () =>
              openContactModal(() => {
                // Refresh contacts list when a new contact is added
                myContacts.mutate()
              }),
            actionOption: (
              <>
                <LuCirclePlus size="24" strokeWidth={1} />
                <FormattedMessage {...messages.organizationToAddNew} />
              </>
            ),
            isDisabled: shareToExtend ? true : formState.isSubmitting,
            options,
            // @ts-expect-error Passing type info forward is not supported
            isOptionDisabled(option: SelectOption<string | number, { id: string }>) {
              // Only disable when an actual selected asset (non-null) belongs to this organization
              return selectedAssets?.some((sa) => sa.asset && sa.ownerId === option.value)
            },
          }}
        />
      )}
    </Container>
  )
}
